import { useLayoutEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { AdaptiveImageryLayer, IMAGERY_MAX_ZOOM, isArcGisTileUrl } from "../utils/adaptiveImagery";

// The editor keeps local metres, while Leaflet uses projected Mercator metres.
// Use the same fixed latitude scale for both axes as the existing plan geometry.
export function planMapView(origin, bounds, width) {
  const [lon, lat] = origin;
  const scale = Math.cos(lat * Math.PI / 180);
  const anchor = L.CRS.EPSG3857.project(L.latLng(lat, lon));
  const centre = L.CRS.EPSG3857.unproject(L.point(
    anchor.x + (bounds.x + bounds.width / 2) / scale,
    anchor.y - (bounds.y + bounds.height / 2) / scale));
  const zoom = Math.log2(2 * Math.PI * L.Projection.SphericalMercator.R * scale * width / (256 * bounds.width));
  return { centre, zoom };
}

export default function PlanMapBackground({ origin, bounds, width, height, onError }) {
  const node = useRef(null), mapRef = useRef(null), errorRef = useRef(onError);
  const [lon, lat] = origin;
  const { x, y, width: extent, height: extentHeight } = bounds;
  errorRef.current = onError;
  useLayoutEffect(() => {
    // Pointer gestures belong to the drawing overlay. Leaflet manages only
    // imagery, tile buffering and zoom transforms, just as on Location.
    const map = L.map(node.current, { zoomControl: false, attributionControl: false,
      dragging: false, scrollWheelZoom: false, doubleClickZoom: false,
      boxZoom: false, keyboard: false, touchZoom: false,
      zoomSnap: 0, zoomAnimation: false, fadeAnimation: false, maxZoom: 30 });
    const url = process.env.REACT_APP_MAP_SATELLITE_URL ||
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
    const options = { maxNativeZoom: isArcGisTileUrl(url) ? IMAGERY_MAX_ZOOM : 18,
      maxZoom: 30, updateWhenIdle: true, keepBuffer: 1 };
    const layer = isArcGisTileUrl(url) ? new AdaptiveImageryLayer(url, options) : L.tileLayer(url, options);
    layer.on("tileerror", () => errorRef.current?.());
    layer.addTo(map);
    mapRef.current = map;
    return () => { mapRef.current = null; map.remove(); };
  }, []);
  useLayoutEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.invalidateSize({ pan: false, animate: false });
    const view = planMapView([lon, lat], { x, y, width: extent, height: extentHeight }, width);
    map.setView(view.centre, view.zoom, { animate: false });
  }, [lon, lat, x, y, extent, extentHeight, width, height]);
  return <div ref={node} data-plan-map="true" aria-hidden="true"
    style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0 }} />;
}
